<template>
  <div ref="elRef" :style="{ height: `${height}px`, zIndex: zIndex }">
    <div
      :class="className"
      :style="{
        top: isSticky ? `${stickyTop}px` : '',
        zIndex: zIndex,
        position: position,
        width: width,
        height: `${height}px`
      }"
    >
      <slot>
        <div>sticky</div>
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  onActivated,
  onDeactivated,
  watch,
  nextTick
} from 'vue'

export default defineComponent({
  name: 'Sticky',
  props: {
    stickyTop: {
      type: Number,
      default: 0
    },
    zIndex: {
      type: Number,
      default: 1
    },
    className: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const elRef = ref<HTMLElement | null>(null)
    const active = ref(false)
    const position = ref('')
    const width = ref('')
    const height = ref<number | undefined>(undefined)
    const isSticky = ref(false)

    const handleReset = () => {
      if (!active.value) {
        return
      }
      reset()
    }

    const reset = () => {
      position.value = ''
      width.value = 'auto'
      active.value = false
      isSticky.value = false
    }

    const handleScroll = () => {
      if (!elRef.value) return
      const elWidth = String(elRef.value.getBoundingClientRect().width)
      width.value = elWidth || 'auto'
      const offsetTop = elRef.value.getBoundingClientRect().top
      if (offsetTop < props.stickyTop) {
        sticky()
      } else {
        handleReset()
      }
    }

    const sticky = () => {
      if (active.value) return
      position.value = 'fixed'
      active.value = true
      width.value = `${width.value}px`
      isSticky.value = true
    }

    const handleResize = () => {
      if (isSticky.value && elRef.value) {
        width.value = `${elRef.value.getBoundingClientRect().width}px`
      }
    }

    const deactivate = () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    }

    onMounted(() => {
      nextTick(() => {
        if (props.disabled || !elRef.value) {
          return
        }
        height.value = elRef.value.getBoundingClientRect().height
        window.addEventListener('scroll', handleScroll)
        window.addEventListener('resize', handleResize)
      })
    })

    onDeactivated(() => {
      deactivate()
    })

    onActivated(() => {
      handleScroll()
    })

    watch(
      () => props.disabled,
      (newVal) => {
        if (newVal) {
          deactivate()
          reset()
        }
      }
    )

    return {
      position,
      width,
      height,
      isSticky,
      elRef
    }
  }
})
</script>
